import { EditOutlined, FileOutlined, SaveFilled, SyncOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, message, Modal, Row, Select, Table, TableProps, Typography } from "antd";
import moment from "moment";
import React from "react";
import { httpRequest } from "../../../helpers/api";
import useFetchList from "../../../hooks/useFetchList";
const { Text, Title } = Typography;

type IOption = {
	value: string;
	label: string;
};
type IConfig = {
	key: string;
	value?: string;
};

export type IConfigProps = {
	key: string;
	label: string;
	value?: string | string[];
	type?: "TEXTAREA" | "CHOOSE" | "CHOOSE-MULTIPLE" | "SOCIALMEDIA" | "EMAIL" | "WHATSAPP" | "PERCENTAGE";
	path?: string;
	options?: IOption[];
	setCustomValue?: () => string;
};
type Props = {
	isLoading?: boolean;
	title: string;
	description: string;
	configs: IConfigProps[];
	headerTitleKey?: string;
	headerTitleValue?: string;
	onSuccessUpdate?: () => void;
	prefixKey?: string;
};

/**
 *
 * @param props
 * httpRequest: AxiosInstance
 * isLoading: loading
 * headerTitleKey: Header label for key config (left column) -> e.g CONTACT EMAIL
 * headerTitleValue: Header label for value which inputted by user (right column) -> e.g example@gmail.com
 * prefixKey: will injected to previous of each config key -> e.g companyID "ABC"
 * @returns
 */
export function ListConfig(props: Props) {
	console.info("prefixKey", props.prefixKey);

	const [form] = Form.useForm();
	const [isLoadingUpdate, setIsLoadingUpdate] = React.useState(false);
	const [isModalVisible, setIsModalVisible] = React.useState<boolean>(false);
	const [tmpData, setTmpData] = React.useState<IConfigProps>();

	const configs = React.useMemo(
		() =>
			props.configs.map((item) => ({
				...item,
				key: (props.prefixKey ? props.prefixKey + "__" : "") + item.key,
			})),
		[props.configs, props.prefixKey]
	);
	const { isLoading, data, fetchList } = useFetchList<IConfig, any>({
		endpoint: "app-configs",
		initialQuery: {
			keys: encodeURIComponent(configs.map((item) => item.key).join(",")),
		},
		limit: props.configs.length,
	});

	const handleClickEdit = (record: any) => {
		if (record.type === "CHOOSE-MULTIPLE") {
			const value: string[] = (record.value || "").split(",").filter((item: string) => !!item) || [];
			setTmpData({
				...(configs.find((item) => item.key === record.key) || {}),
				...record,
				value: value,
			});
			form.setFieldsValue({
				key: record.key,
				value: value,
			});
		} else if (record.type === "CHOOSE") {
			setTmpData({
				...(configs.find((item) => item.key === record.key) || {}),
				...record,
				value: record.value,
			});
			form.setFieldsValue({
				key: record.key,
				value: record.value,
			});
		} else {
			setTmpData({
				...(configs.find((item) => item.key === record.key) || {}),
				...record,
				value: record.value,
			});
			form.setFieldsValue({
				key: record.key,
				value: record.value,
			});
		}
		setIsModalVisible(true);

		// setSearchParams({});
	};

	const listConfig = React.useMemo(() => {
		const list = configs.map((item) => ({
			...item,
			...(data.find((c) => c.key === item.key) || {}),
		}));

		// const autoOpenKey = searchParams.get("key");

		// if (!isLoading && list.length > 0 && autoOpenKey) {
		// 	const record = list.find((item) => item.key === autoOpenKey);
		// 	if (record && (record.options || []).length > 0) {
		// 		handleClickEdit(record);
		// 	}
		// }

		return list;
	}, [configs, data]);

	const handleSave = async (values: any) => {
		try {
			setIsLoadingUpdate(true);
			const dataToSent: any = {};
			const key = tmpData?.key || values.key;

			if (tmpData?.type === "CHOOSE") {
				dataToSent.value = values.value || "";
			} else if (tmpData?.type === "CHOOSE-MULTIPLE") {
				dataToSent.value = values.value.join(",") || "";
			} else {
				dataToSent.value = values.value || "";
			}

			await httpRequest.patch("/app-configs/" + key, dataToSent);

			message.success("Updated successfully");

			form.resetFields();
			setIsLoadingUpdate(false);
			setIsModalVisible(false);

			setTmpData(undefined);

			fetchList();
			if (props.onSuccessUpdate) {
				props.onSuccessUpdate();
			}
		} catch (error: any) {
			message.error(error.data.message);
			setIsModalVisible(false);
			setTmpData(undefined);
			setIsLoadingUpdate(false);
		}
	};

	const mappingLocationConfig = (key: string) => {
		switch (key) {
			case "DASHBOARD_QUOTE":
			case "DASHBOARD_QUOTE2":
			case "DASHBOARD_QUOTE3":
				return "Dashboard Mobile App";

			case "BANK_NAME":
			case "HQ_FINANCE_NAME":
			case "HQ_FINANCE_EMAIL":
			case "HQ_FINANCE_WHATSAPP":
			case "HQ_COMPANY_NAME":
			case "BANK_ACCOUNT_NAME":
			case "BANK_ACCOUNT_NUMBER":
				return "Billing PDF";
			case "HQ_ADDRESS":
			case "HQ_PHONE":
			case "FACEBOOK_URL":
			case "TWITTER_URL":
			case "INSTAGRAM_URL":
			case "YOUTUBE_URL":
			case "TIKTOK_URL":
				return "Web Footer";
			case "HQ_EMAIL":
				return "Billing PDF & Web Footer";
			case "STAR_AGENT_VIDEO_EMBED":
				return "Star Agent";
			case "ABOUT_US_VIDEO_EMBED":
				return "About Us - Our Story";
			case "HQ_WHATSAPP":
				return "Mobile Whatsapp Button";

			default:
				return "";
		}
	};
	const columns = [
		{
			title: props.headerTitleKey ? props.headerTitleKey : "NAME",
			dataIndex: "label",
			key: "label",
			width: 250,
		},
		{
			title: props.headerTitleValue ? props.headerTitleValue : "VALUE",
			dataIndex: "value",
			key: "value",
			align: "left",
			width: 250,
			render: (value, record: IConfigProps) => {
				if (record.type === "CHOOSE") {
					const findConfig = configs.find((c) => c.key === record.key);

					if (!findConfig) {
						return <></>;
					}

					const findOption = (findConfig.options || []).find((o) => o.value === value);
					if (!findOption) {
						return <></>;
					}

					console.log(findConfig);

					return (
						<div
							style={{
								display: "flex",
								justifyContent: "start",
								alignItems: "start",
								flexDirection: "column",
								maxWidth: 250,
								overflowX: "hidden",
							}}
						>
							<Button
								style={{ fontSize: 12 }}
								icon={<FileOutlined />}
								size="small"
								type="link"
								href={`/${findConfig.path}/${findOption.value}`}
								target="_blank"
							>
								{findOption.label}
							</Button>
						</div>
					);
				} else if (record.type === "CHOOSE-MULTIPLE") {
					const findConfig = configs.find((c) => c.key === record.key);

					if (!findConfig) {
						return <></>;
					}

					const findOptions =
						findConfig.options && value
							? (findConfig.options || []).filter((o) => value.includes(o.value))
							: [];

					if (findOptions.length === 0) {
						return <></>;
					}

					return (
						<div
							style={{
								display: "flex",
								justifyContent: "start",
								alignItems: "start",
								flexDirection: "column",
								maxWidth: 250,
								overflowX: "hidden",
							}}
						>
							{findOptions.map((option) => (
								<Button
									style={{ fontSize: 12 }}
									key={option.value}
									icon={<FileOutlined />}
									type="link"
									size="small"
									href={`/${findConfig.path}/${option.value}`}
									target="_blank"
								>
									{option.label}
								</Button>
							))}
						</div>
					);
				} else if (record.type === "PERCENTAGE") {
					return value + "%";
				} else {
					return value;
				}
			},
		},
		{
			title: "LOCATION",
			dataIndex: "key",
			key: "key",
			align: "left",
			render: (key: any) => mappingLocationConfig(key),
		},
		{
			title: "UPDATED AT",
			dataIndex: "updatedAt",
			key: "updatedAt",
			align: "left",
			render: (updatedAt: any) => moment(updatedAt).format("DD MMM YYYY HH:mm"),
		},
		{
			title: "ACTION",
			key: "action",
			align: "center",
			render: (_: any, record: IConfigProps) =>
				record.setCustomValue ? (
					<Button
						onClick={() => {
							console.log(record);
							if (record && record?.setCustomValue) {
								const newValue = record?.setCustomValue();
								if (newValue) {
									handleSave({
										key: record.key,
										value: newValue,
									});
								}
							}
						}}
						style={{ background: "transparent", border: "none" }}
						icon={<SyncOutlined style={{ color: "#A5B2BD" }} />}
					></Button>
				) : (
					<Button
						onClick={() => {
							console.log(record);
							handleClickEdit(record);
						}}
						style={{ background: "transparent", border: "none" }}
						icon={<EditOutlined style={{ color: "#A5B2BD" }} />}
					></Button>
				),
		},
	] as TableProps<any>["columns"];

	return (
		<Row>
			<Col span={5}>
				<Title level={5}>{props.title}</Title>
				<Text style={{ color: "#768499" }}>{props.description}</Text>
			</Col>
			<Col
				offset={1}
				span={16}
			>
				<Table
					rowKey="configId"
					loading={isLoading}
					columns={columns}
					dataSource={listConfig}
					pagination={false}
					bordered
				/>
			</Col>

			<Modal
				open={isModalVisible && tmpData?.key ? true : false}
				onCancel={() => {
					setIsModalVisible(false);
					setTmpData(undefined);
				}}
				closable
				title={tmpData?.label}
				footer={false}
			>
				<Form
					form={form}
					onFinish={handleSave}
					// initialValues={{ key: tmpData?.key, value: tmpData?.value }}
					style={{
						paddingTop: 20,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
					}}
				>
					{tmpData?.type === "CHOOSE" ? (
						<Form.Item name="value">
							<Select
								showSearch
								placeholder="Select one"
								optionFilterProp="label"
								// onChange={(value: string) =>
								//   setTmpData({
								//     ...tmpData,
								//     value,
								//   })
								// }
								// value={tmpData.value ? tmpData.value as string : null}
								allowClear
								options={tmpData.options}
							/>
						</Form.Item>
					) : tmpData?.type === "CHOOSE-MULTIPLE" ? (
						<Form.Item name="value">
							<Select
								mode="multiple"
								showSearch
								placeholder="Select multiple"
								optionFilterProp="label"
								// onChange={(value: string[]) => {
								//   // let data = ''
								//   // if (Array.isArray(value) && value.length > 1) {
								//   //   data = value.join(',')
								//   // } else {
								//   //   data = value[0]
								//   // }
								//   console.log(tmpData)
								//   setTmpData({
								//     ...tmpData,
								//     value: value,
								//   })
								// }}
								// value={(tmpData.value ? tmpData.value : []) as string[]}
								allowClear
								options={tmpData.options}
							/>
						</Form.Item>
					) : tmpData?.type === "SOCIALMEDIA" ? (
						<Form.Item name="value">
							<Input prefix={generateInputPrefix(tmpData)} />
						</Form.Item>
					) : tmpData?.type === "TEXTAREA" ? (
						<Form.Item name="value">
							<Input.TextArea />
						</Form.Item>
					) : tmpData?.type === "PERCENTAGE" ? (
						<Form.Item name="value">
							<Input
								type="number"
								step={0.00000001}
								min={0}
								max={100}
								suffix="%"
							/>
						</Form.Item>
					) : (
						<Form.Item name="value">
							<Input />
						</Form.Item>
					)}

					<Button
						type="primary"
						htmlType="submit"
						loading={isLoadingUpdate}
						icon={<SaveFilled />}
					>
						SAVE
					</Button>
				</Form>
			</Modal>
		</Row>
	);
}

function generateInputPrefix(config: IConfigProps, params?: { withHttps?: boolean }) {
	const key = config.key.toLowerCase();
	let preText = "";
	if (params?.withHttps) {
		preText = "https://";
	}
	if (key.includes("instagram")) {
		return preText + "instagram.com/";
	} else if (key.includes("facebook")) {
		return preText + "facebook.com/";
	} else if (key.includes("youtube")) {
		return preText + "youtube.com/";
	} else if (key.includes("tiktok")) {
		return preText + "tiktok.com/";
	}
	return preText;
}
